<template>
  <div class="hotel-price-rule-view">
    <StandardSearchPageView
      ref="table"
      rowKey="id"
      :colNum="4"
      :labelColSpan="8"
      :wrapperColSpan="16"
      :columns="tableColumns"
      :queryParams="queryParams"
      :searchOptions="searchOptions"
      :operationBoxStyle="{ padding: '0 0 16px' }"
      :requestPromise="loadDataList"
    >
      <a-button slot="operation" type="primary" icon="plus" @click="onAddEvent">
        新建价格规则
      </a-button>
      <template v-slot:rateplanNameSlot="{ text }">
        <ellipsis :length="30" tooltip>{{ text }}</ellipsis>
      </template>
      <template v-slot:action="{ record }">
        <a-space>
          <a @click="onEditEvent(record)">编辑</a>
          <a-popconfirm
            :title="Number(record.status) === 1 ? '确认禁用该价格规则？' : '确认启用该价格规则？'"
            @confirm="onSwitchStatus(record, Number(record.status) === 1 ? 0 : 1)"
          >
            <a>{{ Number(record.status) === 1 ? '禁用' : '启用' }}</a>
          </a-popconfirm>
        </a-space>
      </template>
    </StandardSearchPageView>

    <SimpleFormEditorDialog
      v-if="showDialog"
      ref="formEditor"
      :editConfigList="currentEditConfigList"
      :item="selectItem"
      :rules="rules"
      :title="selectItem && selectItem.id ? '编辑价格规则' : '新建价格规则'"
      :width="720"
      @cancel="showDialog = false"
      @ok="onEditResultEvent"
    >
      <template v-slot:accountId="{ form }">
        <a-select
          v-model="form.accountId"
          :options="accountOptions"
          allowClear
          placeholder="请选择大横琴账号"
          style="width: 100%"
          @change="onAccountChange(form)"
        />
      </template>
      <template v-slot:hotelId="{ form }">
        <a-select
          v-model="form.hotelId"
          :options="hotelOptions"
          allowClear
          show-search
          :filter-option="false"
          :default-active-first-option="false"
          placeholder="请选择酒店名称"
          style="width: 100%"
          @search="onHotelSearch"
          @change="(value) => onHotelChange(form, value)"
        />
      </template>
      <template v-slot:rateplanId="{ form }">
        <a-select
          v-model="form.rateplanId"
          :options="rateplanOptions"
          allowClear
          :disabled="!form.hotelId"
          :placeholder="form.hotelId ? '请选择价格计划名称' : '请先选择酒店名称'"
          style="width: 100%"
        />
      </template>
      <template v-slot:dateRange="{ form }">
        <a-range-picker
          v-model="form.dateRange"
          style="width: 100%"
          valueFormat="YYYY-MM-DD"
          format="YYYY-MM-DD"
        />
      </template>
      <template v-slot:calcType="{ form }">
        <a-select
          v-model="form.calcType"
          :options="calcTypeOptions"
          allowClear
          placeholder="请选择计算方式"
          style="width: 100%"
          @change="() => onCalcTypeChange(form)"
        />
      </template>
      <template v-slot:adjustValue="{ form }">
        <a-input-number
          v-model="form.adjustValue"
          :precision="2"
          :min="Number(form.calcType) === 2 ? 0 : undefined"
          :max="Number(form.calcType) === 2 ? 100 : undefined"
          :placeholder="Number(form.calcType) === 2 ? '请输入销售价比例，0-100' : '请输入调整金额，允许负数'"
          style="width: 100%"
        />
        <div v-if="Number(form.calcType) === 2" class="field-tip">单位：百分比，范围 0-100，例如 98 表示结算价的 98%</div>
        <div v-else-if="Number(form.calcType) === 1" class="field-tip">单位：元，固定金额加减，允许负数</div>
      </template>
    </SimpleFormEditorDialog>
  </div>
</template>

<script>
import SimpleFormEditorDialog from '@/customComponents/SimpleFormEditorDialog.vue'
import StandardSearchPageView from '@/customComponents/standard/StandardSearchPageView.vue'
import {
  CALC_TYPE_OPTIONS,
  editConfigList,
  searchOptions,
  tableColumns
} from '../config/hotelPriceRule.config.js'

function formatAdjustValue (item = {}) {
  if (Number(item.calculationType) === 2) {
    return item.priceRate != null && item.priceRate !== '' ? `${item.priceRate}%` : ''
  }
  return item.adjustmentAmount != null && item.adjustmentAmount !== '' ? item.adjustmentAmount : ''
}

function getCalcTypeName (calcType) {
  const matched = CALC_TYPE_OPTIONS.find(item => item.value === calcType)
  return matched ? matched.label : ''
}

export default {
  name: 'HotelPriceRuleComponent',
  components: {
    SimpleFormEditorDialog,
    StandardSearchPageView
  },
  data () {
    return {
      showDialog: false,
      selectItem: undefined,
      queryParams: {},
      accountOptions: [],
      hotelOptions: [],
      rateplanOptions: [],
      hotelSearchTimer: null,
      searchOptions,
      tableColumns,
      editConfigList,
      calcTypeOptions: CALC_TYPE_OPTIONS,
      rules: {
        accountId: [{ required: true, message: '请选择大横琴账号' }],
        ruleName: [{ required: true, message: '请输入规则名称' }],
        hotelId: [{ required: true, message: '请选择酒店名称' }],
        rateplanId: [{ required: true, message: '请选择价格计划名称' }],
        dateRange: [{ required: true, message: '请选择生效日期' }],
        calcType: [{ required: true, message: '请选择计算方式' }],
        adjustValue: [
          { required: true, message: '请输入调整值' },
          {
            validator: (rule, value, callback) => {
              const calcType = this.$refs.formEditor &&
                this.$refs.formEditor.form &&
                this.$refs.formEditor.form.calcType
              if (Number(calcType) === 2 && value != null && (Number(value) < 0 || Number(value) > 100)) {
                callback(new Error('销售价比例需在 0-100 之间'))
                return
              }
              callback()
            }
          }
        ]
      }
    }
  },
  computed: {
    currentEditConfigList () {
      const calcType = this.selectItem && this.selectItem.calcType
      return this.editConfigList.map(item => {
        if (item.key !== 'adjustValue') return item
        if (Number(calcType) === 2) {
          return {
            ...item,
            name: '销售价比例（%）',
            placeholder: '请输入销售价比例'
          }
        }
        return {
          ...item,
          name: '调整金额（元）',
          placeholder: '请输入调整金额（元）'
        }
      })
    }
  },
  methods: {
    getSourceService () {
      return this.getHttpServices().getSourceService()
    },
    loadAccountOptions () {
      this.getSourceService()
        .post('dahengqin/platform/account_options', {
          merchantId: this.$store.getters.userInfo.merchantId
        })
        .then(res => {
          this.accountOptions = res.map(item => ({
            label: item.channelUserName,
            value: item.id
          }))
        })
        .catch(err => {
          console.error(err)
        })
    },
    loadHotelOptions (keyword) {
      const params = {
        merchantId: this.$store.getters.userInfo.merchantId
      }
      if (keyword) params.keyword = keyword
      return this.getSourceService()
        .post('dahengqin/platform/hotel_options', params)
        .then(res => {
          this.hotelOptions = res.map(item => ({
            label: item.hotelName,
            value: item.proId
          }))
        })
        .catch(err => {
          this.hotelOptions = []
          console.error(err)
        })
    },
    onHotelSearch (keyword) {
      if (this.hotelSearchTimer) clearTimeout(this.hotelSearchTimer)
      this.hotelSearchTimer = setTimeout(() => {
        this.loadHotelOptions(keyword)
      }, 300)
    },
    loadRateplanOptions (hotelId) {
      if (!hotelId) {
        this.rateplanOptions = []
        return Promise.resolve()
      }
      return this.getSourceService()
        .post('dahengqin/platform/rateplan_options', {
          merchantId: this.$store.getters.userInfo.merchantId,
          hotelId
        })
        .then(res => {
          this.rateplanOptions = res.map(item => ({
            label: item.rateplanName,
            value: item.rateplanId
          }))
        })
        .catch(err => {
          this.rateplanOptions = []
          console.error(err)
        })
    },
    onAccountChange (form) {
      this.$set(form, 'hotelId', undefined)
      this.$set(form, 'rateplanId', undefined)
      this.rateplanOptions = []
    },
    onHotelChange (form, value) {
      this.$set(form, 'rateplanId', undefined)
      this.loadRateplanOptions(value)
    },
    onCalcTypeChange (form) {
      this.$set(form, 'adjustValue', undefined)
      if (this.selectItem) {
        this.$set(this.selectItem, 'calcType', form.calcType)
      }
    },
    normalizeListItem (item) {
      const calcType = item.calculationType
      return {
        ...item,
        accountId: item.channelAccountId,
        calcType,
        adjustValue: Number(calcType) === 2 ? item.priceRate : item.adjustmentAmount,
        calcTypeName: item.calculationTypeName || getCalcTypeName(calcType),
        adjustValueText: formatAdjustValue(item),
        accountName: item.accountName || item.channelUserName
      }
    },
    buildFormItem (item) {
      if (!item) {
        return {
          accountId: undefined,
          ruleName: undefined,
          hotelId: undefined,
          rateplanId: undefined,
          dateRange: [],
          calcType: undefined,
          adjustValue: undefined
        }
      }
      const calcType = item.calculationType
      return {
        ...item,
        accountId: item.channelAccountId,
        hotelId: item.proId,
        rateplanId: item.rateplanId,
        calcType,
        adjustValue: Number(calcType) === 2 ? item.priceRate : item.adjustmentAmount,
        dateRange: [item.startDate, item.endDate]
      }
    },
    buildListParams (params = {}) {
      const { limitDto = {}, hotelName, effectiveDate, status, rateplanId } = params
      const requestParams = {
        merchantId: this.$store.getters.userInfo.merchantId,
        pageNum: limitDto.pageNum || 1,
        pageSize: limitDto.pageSize || 20
      }
      if (hotelName) requestParams.hotelName = hotelName
      if (effectiveDate) requestParams.effectiveDate = effectiveDate
      if (status === 0 || status === 1 || status === '0' || status === '1') {
        requestParams.status = Number(status)
      }
      if (rateplanId) requestParams.rateplanId = Number(rateplanId)
      return requestParams
    },
    buildSaveParams (params = {}) {
      const dateRange = params.dateRange || []
      const calculationType = Number(params.calcType)
      const saveParams = {
        merchantId: this.$store.getters.userInfo.merchantId,
        channelAccountId: params.accountId,
        rateplanId: params.rateplanId,
        ruleName: params.ruleName,
        startDate: dateRange[0],
        endDate: dateRange[1],
        calculationType
      }
      if (calculationType === 1) {
        saveParams.adjustmentAmount = params.adjustValue
      } else if (calculationType === 2) {
        saveParams.priceRate = params.adjustValue
      }
      if (params.id != null) {
        saveParams.id = params.id
        saveParams.version = params.version
      }
      return saveParams
    },
    loadDataList (params) {
      return this.getSourceService()
        .post('dahengqin/rateplan-price-rule/list', this.buildListParams(params))
        .then(res => ({
          data: res.data.map(this.normalizeListItem),
          totalCount: res.totalCount,
          pageCount: res.pageCount
        }))
    },
    onAddEvent () {
      this.hotelOptions = []
      this.rateplanOptions = []
      this.selectItem = this.buildFormItem()
      this.showDialog = true
      this.loadAccountOptions()
      this.loadHotelOptions()
    },
    onEditEvent (record) {
      const formItem = this.buildFormItem(record)
      this.selectItem = formItem
      this.showDialog = true
      this.loadAccountOptions()
      this.loadHotelOptions()
      this.loadRateplanOptions(formItem.hotelId)
    },
    onSwitchStatus (record, status) {
      if (!record || record.id == null) {
        this.$message.error('规则ID缺失')
        return
      }
      if (record.version == null) {
        this.$message.error('规则版本号缺失，请刷新列表后重试')
        return
      }
      this.getSourceService()
        .post('dahengqin/rateplan-price-rule/status', {
          merchantId: this.$store.getters.userInfo.merchantId,
          id: record.id,
          status: Number(status),
          version: record.version
        })
        .then(() => {
          this.$message.success(Number(status) === 1 ? '已启用' : '已禁用')
          this.refreshData(false)
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '操作失败，请重试！')
          console.error(err)
        })
    },
    refreshData (resetPage) {
      if (this.$refs.table) {
        this.$refs.table.refresh(resetPage)
      }
    },
    onEditResultEvent (params) {
      const isEdit = !!(params && params.id)
      const requestParams = this.buildSaveParams(params)
      if (isEdit && requestParams.version == null) {
        this.$message.error('规则版本号缺失，请刷新列表后重试')
        return
      }
      const url = isEdit
        ? 'dahengqin/rateplan-price-rule/edit'
        : 'dahengqin/rateplan-price-rule/create'
      this.getSourceService()
        .post(url, requestParams)
        .then(() => {
          this.showDialog = false
          this.selectItem = undefined
          this.$message.success('操作成功！')
          this.refreshData(!isEdit)
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '操作失败，请重试！')
          console.error(err)
        })
    }
  }
}
</script>

<style scoped>
.hotel-price-rule-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.field-tip {
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  line-height: 1.4;
}
</style>
